/**
 * API Service for communicating with the backend
 */

// API base URL - in development, use proxy configured in package.json
// In production, this would be the deployed API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';


const ACCESS_RELOAD_GUARD_KEY = 'access-login-reload-at';
const ACCESS_RELOAD_COOLDOWN_MS = 10_000;

function shouldTriggerAccessReload(credentials: RequestCredentials): boolean {
  if (credentials !== 'include') return false;

  // Avoid reload loops during local development when API auth may be
  // intentionally unavailable.
  const isLocalDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (isLocalDevHost) return false;

  try {
    const lastReloadAt = Number(sessionStorage.getItem(ACCESS_RELOAD_GUARD_KEY) || '0');
    if (Date.now() - lastReloadAt < ACCESS_RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(ACCESS_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Continue and attempt reload even if sessionStorage is unavailable.
  }

  return true;
}

/**
 * Authenticated fetch wrapper.
 * - Sends credentials for same-origin requests and explicit API-relative paths
 * - Omits credentials for cross-origin URLs to avoid CORS failures when the
 *   remote service returns Access-Control-Allow-Origin: *
 * - On 401/403 (session expired), reloads the page to trigger the
 *   Cloudflare Access login flow
 */
async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const requestUrl = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;

  const isAbsoluteHttpUrl = /^https?:\/\//i.test(requestUrl);
  const isSameOrigin = isAbsoluteHttpUrl
    ? (() => {
        try {
          return new URL(requestUrl, window.location.origin).origin === window.location.origin;
        } catch {
          return false;
        }
      })()
    : true;

  const isCrossOrigin = isAbsoluteHttpUrl && !isSameOrigin;
  const defaultCredentials: RequestCredentials = isCrossOrigin ? 'omit' : 'include';

  // Never allow credentialed requests to cross-origin APIs. Several hosted
  // endpoints return `Access-Control-Allow-Origin: *`, which is incompatible
  // with `credentials: include` and causes hard browser CORS failures.
  const credentials: RequestCredentials = isCrossOrigin
    ? 'omit'
    : (init?.credentials ?? defaultCredentials);

  let response: Response;
  try {
    response = await fetch(input, {
      ...init,
      credentials,
    });
  } catch (err) {
    // CORS errors from Cloudflare Access redirecting to login manifest as TypeErrors.
    // When Access session expires, it returns a 302 redirect to cloudflareaccess.com
    // which the browser can't follow cross-origin, causing a CORS TypeError.
    if (err instanceof TypeError && !isCrossOrigin && shouldTriggerAccessReload(credentials)) {
      window.location.reload();
    }
    throw err;
  }

  if ((response.status === 401 || response.status === 403) && shouldTriggerAccessReload(credentials)) {
    // Session expired or forbidden on credentialed requests — reload to trigger Access login
    window.location.reload();
    // Return the response in case reload is delayed
    return response;
  }

  return response;
}

export interface ContractFromAPI {
  id: string;
  name: string;
  state: string;
  status: string;
  effective_date: string | null;
  expiration_date: string | null;
  version: string | null;
  document_url: string | null;
  created_at: string;
  updated_at: string;
  analysis_status?: string;
  latest_analysis_id?: string;
  gap_count?: number;
}

export interface ContractsListResponse {
  success: boolean;
  error?: string;
  contracts: ContractFromAPI[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: {
    state: string | null;
    status: string | null;
    search: string | null;
  };
  sorting: {
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
  filterOptions?: {
    states: string[];
    statuses: string[];
  };
}

export interface UploadResponse {
  success: boolean;
  documentId: string;
  filename: string;
  size: number;
  contentType: string;
  r2Path: string;
  supabaseId?: string;
}

export interface UploadError {
  error: string;
  details?: string;
  documentId?: string;
}

export interface FetchContractsParams {
  page?: number;
  limit?: number;
  state?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Fetch contracts from the API
 */
export async function fetchContracts(params: FetchContractsParams = {}): Promise<ContractsListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.state) searchParams.set('state', params.state);
  if (params.status) searchParams.set('status', params.status);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/contracts${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching contracts from:', url);

  const response = await apiFetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch contracts' }));
    console.error('[API] Fetch contracts failed:', error);
    throw new Error(error.error || 'Failed to fetch contracts');
  }

  const data: ContractsListResponse = await response.json();
  console.log('[API] Contracts response:', data);

  // Check if the API returned an error in the response body
  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Upload a contract document
 */
export async function uploadContract(
  file: File,
  metadata?: {
    name?: string;
    state?: string;
    effectiveDate?: string;
    expirationDate?: string;
    version?: string;
  }
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  if (metadata?.name) formData.append('name', metadata.name);
  if (metadata?.state) formData.append('state', metadata.state);
  if (metadata?.effectiveDate) formData.append('effectiveDate', metadata.effectiveDate);
  if (metadata?.expirationDate) formData.append('expirationDate', metadata.expirationDate);
  if (metadata?.version) formData.append('version', metadata.version);

  console.log('[API] Uploading contract:', file.name);

  const response = await apiFetch(`${API_BASE_URL}/api/documents`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error: UploadError = await response.json().catch(() => ({ error: 'Upload failed' }));
    console.error('[API] Upload failed:', error);
    throw new Error(error.error || 'Upload failed');
  }

  const data: UploadResponse = await response.json();
  console.log('[API] Upload response:', data);

  // Check if supabaseId is present - if not, the contract wasn't saved to DB
  if (!data.supabaseId) {
    console.warn('[API] Warning: Contract was uploaded to R2 but not saved to Supabase database');
  }

  return data;
}

/**
 * Process a contract document (extract text with Document AI)
 */
export async function processContract(contractId: string): Promise<{ success: boolean; error?: string }> {
  console.log('[API] Processing contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/process`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Process response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to process contract');
  }

  return data;
}

/**
 * Delete a contract and all associated data (R2 files, Supabase records, KV jobs)
 */
export async function deleteContract(contractId: string): Promise<{ success: boolean; deletedItems?: string[]; errors?: string[]; error?: string }> {
  console.log('[API] Deleting contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}`, {
    method: 'DELETE',
  });

  const data = await response.json();
  console.log('[API] Delete response:', data);

  // 207 = partial success (contract deleted from primary DB but a secondary system like Neo4j failed)
  if (response.status === 207) {
    if (data.errors?.length) {
      console.warn('[API] Delete partial success, secondary errors:', data.errors);
    }
    return data;
  }

  if (!response.ok || !data.success) {
    throw new Error(data.error || data.message || 'Failed to delete contract');
  }

  return data;
}

/**
 * Document status response from API
 */
export interface ContractStatusResponse {
  success: boolean;
  documentId: string;
  name: string;
  status: string;
  updatedAt: string;
  createdAt: string;
  error?: string;
  needsTransferToR2?: boolean;
  gcsOutputUri?: string;
  needsStructureRetrieval?: boolean;
  extractionError?: string;
  transferProgress?: {
    current: number;
    total: number;
    processedUpTo?: number;
  };
  stale?: boolean;
  staleSinceMinutes?: number;
  autoHealed?: boolean;
}

/**
 * Get the current status of a contract document (for polling during processing)
 */
export async function getContractStatus(contractId: string): Promise<ContractStatusResponse> {
  console.log('[API] Getting contract status:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/status`);
  const data = await response.json();
  console.log('[API] Status response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get contract status');
  }

  return data;
}

/**
 * Start GCS-to-R2 transfer - download OCR results from GCS and store in R2
 * Called when status returns needsTransferToR2: true
 */
export async function startGCSTransfer(contractId: string): Promise<{ success: boolean; status?: string; error?: string }> {
  console.log('[API] Starting GCS-to-R2 transfer for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/transfer-to-r2`, {
    method: 'POST',
  });
  const data = await response.json();
  console.log('[API] GCS-to-R2 transfer response:', data);

  return data;
}

/**
 * Auto-recover a stuck contract (stuck in 'processing' for too long).
 * Checks R2 for existing artifacts to heal, or resets and reprocesses via Document AI.
 */
export async function autoRecoverContract(contractId: string): Promise<{
  success: boolean;
  action?: string;
  message?: string;
  error?: string;
}> {
  console.log('[API] Auto-recovering contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/auto-recover`, {
    method: 'POST',
  });
  const data = await response.json();
  console.log('[API] Auto-recover response:', data);

  return data;
}

/**
 * Structure extraction response from API
 */
export interface ExtractStructureResponse {
  success: boolean;
  documentId: string;
  structure?: {
    title: string;
    effectiveDate: string;
    parties: string[];
    articles: Array<{
      number: string;
      title: string;
      sections: Array<{
        number: string;
        title: string;
        clauses: Array<{
          number: string;
          text: string;
          type: string;
        }>;
      }>;
    }>;
  };
  processingTime?: number;
  tokensUsed?: number;
  error?: string;
}

/**
 * Extract contract structure using Claude AI
 */
export async function extractStructure(contractId: string): Promise<ExtractStructureResponse> {
  console.log('[API] Extracting structure for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/extract-structure`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Extract structure response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to extract structure');
  }

  return data;
}

/**
 * Get extracted structure from R2 after async extraction completes
 * Call this after status becomes 'extraction_complete'
 */
export async function getExtractedStructure(contractId: string): Promise<ExtractStructureResponse> {
  console.log('[API] Getting extracted structure for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/extracted-structure`, {
    method: 'GET',
  });

  const data = await response.json();
  console.log('[API] Get extracted structure response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get extracted structure');
  }

  return data;
}

// =============================================================================
// Two-Phase Structure Extraction (faster, avoids timeouts)
// =============================================================================

/**
 * TOC article entry - lightweight article info (legacy format)
 */
export interface TOCArticle {
  number: string;
  title: string;
}

/**
 * TOC Entry - comprehensive entry for any hierarchical contract structure
 * Supports ARTICLE, PART, CHAPTER, numbered sections (2.1, 2.1.1), lettered sections, etc.
 */
export interface TOCEntry {
  id: string;                  // Unique identifier
  number: string;              // Display number as shown (e.g., "2.1", "I", "A")
  title: string;               // Title text
  level: number;               // Hierarchy level (0 = top, 1 = child, etc.)
  type: 'article' | 'part' | 'chapter' | 'section' | 'subsection' | 'appendix' | 'exhibit' | 'other';
  pageNumber?: number;         // Page number if in TOC
  parentId?: string;           // Parent entry's id
  numberingFormat?: 'roman' | 'arabic' | 'letter' | 'decimal' | 'mixed';
}

/**
 * Metadata about TOC extraction
 */
export interface TOCMetadata {
  totalEntries: number;
  maxDepth: number;
  formats: string[];
}

/**
 * Response from TOC extraction endpoint
 */
export interface ExtractTOCResponse {
  success: boolean;
  documentId: string;
  entries?: TOCEntry[];        // NEW: full hierarchical entries (flexible format)
  articles: TOCArticle[];      // KEEP: backward compatibility
  cached: boolean;
  processingTime?: number;
  tokensUsed?: { input: number; output: number };
  metadata?: TOCMetadata;      // NEW: extraction metadata
  error?: string;
}

/**
 * Extracted article with full details
 */
export interface ExtractedArticle {
  number: string;
  title: string;
  summary?: string;
  sections: Array<{
    number: string;
    title: string;
    clauses: Array<{
      number?: string;
      text: string;
      cfrCitations?: string[];
    }>;
  }>;
}

/**
 * Response from article extraction endpoint
 */
export interface ExtractArticleResponse {
  success: boolean;
  documentId: string;
  articleNumber: string;
  article: ExtractedArticle;
  cached: boolean;
  processingTime?: number;
  tokensUsed?: { input: number; output: number };
  error?: string;
}

/**
 * Complete structure after merging all articles
 */
export interface MergedStructure {
  contractName?: string;
  effectiveDate?: string;
  expirationDate?: string;
  state?: string;
  version?: string;
  articles: ExtractedArticle[];
  totalClauses: number;
  extractionConfidence: number;
}

/**
 * Response from merge structure endpoint
 */
export interface MergeStructureResponse {
  success: boolean;
  documentId: string;
  structure?: MergedStructure;
  totalClauses?: number;
  contractName?: string;
  articlesExtracted: number;
  missingArticles?: string[];
  missingArticleCount?: number;
  processingTime?: number;
  error?: string;
}

/**
 * Extract Table of Contents (Phase 1 of two-phase extraction)
 * Fast (~12 seconds), returns article list
 * @param contractId - Document ID
 * @param force - If true, skip cache and re-extract
 */
export async function extractTOCFromContract(contractId: string, force: boolean = false): Promise<ExtractTOCResponse> {
  console.log('[API] Extracting TOC for contract:', contractId, force ? '(force re-extract)' : '');

  const url = force
    ? `${API_BASE_URL}/api/documents/${contractId}/extract-toc?force=true`
    : `${API_BASE_URL}/api/documents/${contractId}/extract-toc`;

  const response = await apiFetch(url, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Extract TOC response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to extract TOC');
  }

  return data;
}

/**
 * Helper function to delay for a given number of milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff on NetworkError / TypeError.
 * Does NOT retry on server errors with a valid response (4xx/5xx) — only on
 * fetch-level failures like connection resets and timeouts.
 */
async function retryOnNetworkError<T>(
  fn: () => Promise<T>,
  label: string,
  maxRetries = 3,
  baseDelayMs = 2000,
): Promise<T> {
  let lastError: Error | undefined;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const isNetworkError =
        err instanceof TypeError &&
        (err.message.includes('NetworkError') || err.message.includes('Failed to fetch') || err.message.includes('network'));
      if (!isNetworkError || attempt === maxRetries) {
        throw err;
      }
      lastError = err as Error;
      const delayMs = baseDelayMs * Math.pow(2, attempt);
      console.warn(`[Retry] ${label} failed with NetworkError (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delayMs}ms...`, err);
      await delay(delayMs);
    }
  }
  throw lastError || new Error(`${label} failed after retries`);
}

/**
 * Extract single article details (Phase 2 of two-phase extraction)
 * Moderate (~8 seconds each), returns sections and clauses for one article
 * Includes retry logic with exponential backoff for 503 errors
 */
export async function extractArticleFromContract(
  contractId: string,
  articleNumber: string,
  maxRetries: number = 3
): Promise<ExtractArticleResponse> {
  console.log('[API] Extracting article', articleNumber, 'for contract:', contractId);

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      // Exponential backoff with longer delays: 5s, 15s, 45s
      // This gives the Cloudflare Worker more time to recover from 503 errors
      const backoffMs = Math.pow(3, attempt) * 5000;
      console.log(`[API] Retry ${attempt}/${maxRetries} for article ${articleNumber} after ${backoffMs}ms`);
      await delay(backoffMs);
    }

    try {
      const response = await apiFetch(
        `${API_BASE_URL}/api/documents/${contractId}/extract-article/${encodeURIComponent(articleNumber)}`,
        { method: 'POST' }
      );

      // Retry on 503 Service Unavailable (worker overloaded)
      if (response.status === 503) {
        console.warn(`[API] Got 503 for article ${articleNumber}, will retry`);
        lastError = new Error(`Service unavailable (503) for article ${articleNumber}`);
        continue;
      }

      const data = await response.json();
      console.log('[API] Extract article response:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to extract article ${articleNumber}`);
      }

      return data;
    } catch (error) {
      // Network errors (including CORS from 503) should trigger retry
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.warn(`[API] Network error for article ${articleNumber}, will retry`);
        lastError = error;
        continue;
      }
      throw error;
    }
  }

  throw lastError || new Error(`Failed to extract article ${articleNumber} after ${maxRetries} retries`);
}

/**
 * Merge all extracted articles into complete structure
 * Fast (~1 second), combines cached article extractions
 */
export async function mergeContractStructure(contractId: string, options?: { compact?: boolean }): Promise<MergeStructureResponse> {
  console.log('[API] Merging structure for contract:', contractId, options?.compact ? '(compact)' : '');

  const compact = options?.compact ? '?compact=true' : '';
  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/merge-structure${compact}`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Merge structure response:', {
    success: data.success,
    articlesExtracted: data.articlesExtracted,
    totalClauses: data.totalClauses,
    missingArticleCount: data.missingArticleCount,
    processingTime: data.processingTime,
  });

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to merge structure');
  }

  return data;
}

/**
 * Progress callback for orchestrated extraction
 */
export type ExtractionProgressCallback = (
  phase: string,
  progress: number,
  detail?: string
) => void;

/**
 * Options for orchestrated structure extraction
 */
export interface OrchestratedExtractionOptions {
  /** Callback for progress updates */
  onProgress?: ExtractionProgressCallback;
  /** Maximum articles to extract in parallel (default: 2 to avoid worker overload) */
  batchSize?: number;
  /** Whether to store the final structure in Supabase (default: true) */
  storeStructure?: boolean;
  /** Force re-extraction, skipping cache (default: false) */
  forceReextract?: boolean;
  /** AbortSignal to cancel in-flight work when the modal closes/reopens */
  signal?: AbortSignal;
}

// =============================================================================
// Async Queue-Based Article Extraction (avoids 503 errors)
// =============================================================================

/**
 * Response from queue-extraction endpoint
 */
export interface QueueExtractionResponse {
  success: boolean;
  documentId: string;
  jobId?: string;
  totalArticles?: number;
  status?: string;
  message?: string;
  error?: string;
}

/**
 * Response from process-next-article endpoint
 */
export interface ProcessNextArticleResponse {
  success: boolean;
  documentId: string;
  articleNumber?: string;
  cached?: boolean;
  status: string;
  completedArticles: number;
  failedArticles: number;
  totalArticles: number;
  processingTime?: number;
  error?: string;
  /** true if this article is being extracted in multiple chunks */
  chunked?: boolean;
  /** total number of chunks for this article */
  totalChunks?: number;
  /** number of chunks completed so far */
  completedChunks?: number;
}

/**
 * Response from extraction-status endpoint
 */
export interface ExtractionStatusResponse {
  success: boolean;
  documentId: string;
  status: string;
  totalArticles: number;
  completedArticles: number;
  failedArticles: number;
  currentArticle?: string;
  articles?: Array<{
    number: string;
    title: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    error?: string;
  }>;
  error?: string;
}

/**
 * Queue all articles for async extraction
 * Returns immediately - use processNextArticle to process articles one at a time
 */
export async function queueExtraction(contractId: string): Promise<QueueExtractionResponse> {
  console.log('[API] Queueing extraction for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/queue-extraction`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Queue extraction response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to queue extraction');
  }

  return data;
}

/**
 * Process next article from queue
 * Call this repeatedly until status is 'completed'
 * Includes retry logic for 503 errors with exponential backoff
 */
export async function processNextArticle(
  contractId: string,
  maxRetries: number = 5
): Promise<ProcessNextArticleResponse> {
  console.log('[API] Processing next article for contract:', contractId);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/process-next-article`, {
        method: 'POST',
      });

      // Handle 503 with retry
      if (response.status === 503) {
        const waitMs = Math.pow(2, attempt) * 5000; // 5s, 10s, 20s, 40s, 80s
        console.warn(`[API] Got 503 on attempt ${attempt + 1}/${maxRetries + 1}, waiting ${waitMs / 1000}s before retry`);
        if (attempt < maxRetries) {
          await delay(waitMs);
          continue;
        }
        // Last attempt failed, return error response
        return {
          success: false,
          documentId: contractId,
          status: 'error',
          completedArticles: 0,
          failedArticles: 0,
          totalArticles: 0,
          error: 'Service unavailable (503) after multiple retries',
        };
      }

      const data = await response.json();
      console.log('[API] Process next article response:', data);
      return data;
    } catch (error) {
      // Network errors (including CORS from 503) should trigger retry
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        const waitMs = Math.pow(2, attempt) * 5000;
        console.warn(`[API] Network error on attempt ${attempt + 1}/${maxRetries + 1}, waiting ${waitMs / 1000}s before retry`);
        if (attempt < maxRetries) {
          await delay(waitMs);
          continue;
        }
      }
      // Return error response instead of throwing
      console.error('[API] Process next article error:', error);
      return {
        success: false,
        documentId: contractId,
        status: 'error',
        completedArticles: 0,
        failedArticles: 0,
        totalArticles: 0,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Should not reach here, but just in case
  return {
    success: false,
    documentId: contractId,
    status: 'error',
    completedArticles: 0,
    failedArticles: 0,
    totalArticles: 0,
    error: 'Max retries exceeded',
  };
}

/**
 * Get extraction job status
 */
export async function getExtractionStatus(contractId: string): Promise<ExtractionStatusResponse> {
  console.log('[API] Getting extraction status for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/extraction-status`);
  const data = await response.json();
  console.log('[API] Extraction status response:', data);

  return data;
}

// =============================================================================
// Batch Extraction API (Anthropic Message Batches)
// =============================================================================

/**
 * Response from submit-extraction-batch endpoint
 */
export interface SubmitBatchResponse {
  success: boolean;
  documentId: string;
  batchId?: string;
  totalArticles?: number;
  error?: string;
}

/**
 * Response from batch-status endpoint
 */
export interface BatchStatusResponse {
  success: boolean;
  documentId: string;
  batchId?: string;
  processingStatus?: string;
  succeeded?: number;
  errored?: number;
  expired?: number;
  processing?: number;
  canceled?: number;
  total?: number;
  endedAt?: string;
  error?: string;
  batchNotFound?: boolean;
  submittedAt?: string | null;
  isStale?: boolean;
  batchAgeMinutes?: number | null;
}

/**
 * Response from process-batch-results endpoint
 */
export interface ProcessBatchResultsResponse {
  success: boolean;
  documentId: string;
  stored?: number;
  failed?: number;
  remaining?: number;
  complete?: boolean;
  nextOffset?: number;
  error?: string;
}

/**
 * Submit all articles as a single Anthropic batch for extraction
 */
export async function submitExtractionBatch(contractId: string): Promise<SubmitBatchResponse> {
  console.log('[API] Submitting extraction batch for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/submit-extraction-batch`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Submit batch response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to submit extraction batch');
  }

  return data;
}

/**
 * Check the status of the Anthropic batch
 */
export async function getBatchStatus(contractId: string): Promise<BatchStatusResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/batch-status`);
  const data = await response.json();
  return data;
}

/**
 * Retrieve batch results and store article JSONs in R2
 * Call with offset=0 first, then with nextOffset if remaining > 0
 */
export async function processBatchResults(contractId: string, offset: number = 0): Promise<ProcessBatchResultsResponse> {
  console.log(`[API] Processing batch results for contract: ${contractId}, offset: ${offset}`);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/process-batch-results?offset=${offset}`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Process batch results response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to process batch results');
  }

  return data;
}

/**
 * Cancel a stuck/stale Anthropic batch and clean up R2 artifacts
 */
export async function cancelBatch(contractId: string): Promise<{ success: boolean; cancelledBatchId?: string; error?: string }> {
  console.log('[API] Cancelling batch for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/cancel-batch`, {
    method: 'POST',
  });

  const data = await response.json();
  console.log('[API] Cancel batch response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to cancel batch');
  }

  return data;
}

/**
 * Orchestrated structure extraction using Anthropic Message Batches API
 *
 * This function coordinates the entire extraction process:
 * 1. Extract TOC to get article list (~12 sec)
 * 2. Queue extraction to pre-compute boundaries (immediate)
 * 3. Submit batch to Anthropic, poll until complete, retrieve results
 * 4. Merge results into complete structure
 * 5. Optionally store in Supabase
 *
 * Uses batch API for 50% cost savings and no timeout risk
 */
export async function extractStructureOrchestrated(
  contractId: string,
  options: OrchestratedExtractionOptions = {}
): Promise<MergedStructure> {
  const { onProgress, storeStructure: shouldStore = true, forceReextract = false, signal } = options;

  /** Throw if the caller has aborted (e.g. modal closed). */
  function checkAborted() {
    if (signal?.aborted) {
      throw new DOMException('Extraction aborted', 'AbortError');
    }
  }

  // Phase 0: Recover a previously-completed batch (e.g. browser was closed during polling)
  checkAborted();
  try {
    const recoveryStatus = await getBatchStatus(contractId);
    if (recoveryStatus.processingStatus === 'ended' && (recoveryStatus.succeeded || 0) > 0) {
      console.log('[Orchestrated] Found completed batch from previous session — recovering results');
      onProgress?.('Recovering previous batch results...', 10, 'Batch recovery');

      let totalStored = 0;
      let totalFailed = 0;
      let nextOffset: number | undefined = 0;
      while (nextOffset !== undefined) {
        checkAborted();
        const resultPage: ProcessBatchResultsResponse = await retryOnNetworkError(
          () => processBatchResults(contractId, nextOffset!),
          `recoveryProcessBatchResults(offset=${nextOffset})`,
        );
        totalStored += resultPage.stored || 0;
        totalFailed += resultPage.failed || 0;
        nextOffset = resultPage.complete ? undefined : resultPage.nextOffset;
      }

      if (totalStored > 0) {
        console.log(`[Orchestrated] Recovered ${totalStored} articles from previous batch`);
        // Skip to Phase 4 (merge) — jump past extraction
        onProgress?.('Finalizing recovered structure...', 90, 'Merging articles');
        checkAborted();
        const mergeResult = await retryOnNetworkError(
          () => mergeContractStructure(contractId, { compact: true }),
          'mergeContractStructure (recovery)',
        );

        if (shouldStore) {
          onProgress?.('Storing structure...', 98);
          checkAborted();
          await retryOnNetworkError(
            () => storeStructureFromR2(contractId),
            'storeStructureFromR2 (recovery)',
          );
        }

        onProgress?.('Complete!', 100);
        return {
          contractName: mergeResult.contractName,
          articles: [],
          totalClauses: mergeResult.totalClauses || 0,
          extractionConfidence: 1,
        } as MergedStructure;
      }
    }
  } catch (err) {
    // If it's an AbortError, re-throw
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // No batch to recover — continue normally
    console.log('[Orchestrated] No previous batch to recover:', err instanceof Error ? err.message : String(err));
  }

  // Pre-flight: verify processed.json exists in R2 before starting the pipeline
  checkAborted();
  onProgress?.('Verifying document files...', 0, 'Pre-flight check');
  try {
    // Use GET (not HEAD) because the backend R2 download route only handles GET.
    // Add a Range header to avoid downloading the full file.
    const r2Check = await apiFetch(
      `${API_BASE_URL}/api/r2/download?key=contracts/${encodeURIComponent(contractId)}/processed.json`,
      { method: 'GET', headers: { 'Range': 'bytes=0-0' } }
    );
    if (!r2Check.ok && r2Check.status !== 206) {
      // processed.json is missing — the GCS-to-R2 transfer likely didn't complete.
      // Reset status to ocr_complete so the transfer can be re-triggered.
      console.warn('[Orchestrated] processed.json missing, resetting document status to re-trigger transfer');
      try {
        await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/reset-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'ocr_complete', restoreGcsUri: true }),
        });
      } catch (resetErr) {
        console.error('[Orchestrated] Failed to auto-reset document status:', resetErr);
      }
      throw new Error(
        'Processed document not found in storage. The OCR transfer may not have completed. ' +
        'The document status has been reset — please resume processing to re-transfer the OCR results.'
      );
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes('Processed document not found')) {
      throw err;
    }
    // Network or CORS error — log and continue, the extraction endpoints will fail with
    // a clearer error if the file is truly missing.
    console.warn('[Orchestrated] Pre-flight check failed, continuing:', err);
  }

  // Phase 1: Extract TOC
  checkAborted();
  onProgress?.('Extracting table of contents...', 0, 'Phase 1: TOC extraction');
  let tocResult;
  try {
    tocResult = await extractTOCFromContract(contractId, forceReextract);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error(`Phase 1 (TOC extraction) failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  console.log('[Orchestrated] TOC result:', {
    hasEntries: !!tocResult.entries,
    entriesCount: tocResult.entries?.length || 0,
    hasArticles: !!tocResult.articles,
    articlesCount: tocResult.articles?.length || 0,
    metadata: tocResult.metadata,
  });

  // Validate TOC has articles
  const articleCount = tocResult.entries?.length || tocResult.articles?.length || 0;
  if (articleCount === 0) {
    throw new Error('No top-level entries found in contract. The document may use an unsupported format or may not have a recognizable table of contents.');
  }

  // Phase 2: Queue all articles for extraction
  checkAborted();
  onProgress?.('Preparing article boundaries...', 5, 'Phase 2: Boundary detection');
  let queueResult;
  try {
    queueResult = await queueExtraction(contractId);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error(`Phase 2 (boundary detection) failed: ${err instanceof Error ? err.message : String(err)}`);
  }
  console.log('[Orchestrated] Queue result:', queueResult);

  const totalArticles = queueResult.totalArticles || articleCount;

  // Phase 3: Extraction with retry (max 2 retries). Each attempt tries batch first, then queue fallback.
  const PHASE3_MAX_RETRIES = 2;
  let phase3Success = false;

  for (let phase3Attempt = 0; phase3Attempt <= PHASE3_MAX_RETRIES; phase3Attempt++) {
    checkAborted();

    if (phase3Attempt > 0) {
      console.warn(`[Orchestrated] Phase 3 retry ${phase3Attempt}/${PHASE3_MAX_RETRIES}`);
      onProgress?.(`Retrying extraction (attempt ${phase3Attempt + 1})...`, 10, 'Retry');
      await delay(5000);
    }

    try {
      // Phase 3a: Submit batch to Anthropic
      onProgress?.(`Submitting ${totalArticles} articles for extraction...`, 10, 'Batch submission');
      let batchResult;
      let batchFailed = false;

      try {
        batchResult = await retryOnNetworkError(
          () => submitExtractionBatch(contractId),
          'submitExtractionBatch',
        );
        console.log('[Orchestrated] Batch submitted:', batchResult);
      } catch (batchErr) {
        if (batchErr instanceof DOMException && batchErr.name === 'AbortError') throw batchErr;
        console.warn('[Orchestrated] Batch submission failed, falling back to queue-based extraction:', batchErr);
        batchFailed = true;
      }

      if (!batchFailed && batchResult) {
        const batchTotalArticles = batchResult.totalArticles || 0;

        if (batchTotalArticles > 0) {
          // Phase 3b: Poll until complete (every 30 seconds)
          const BATCH_POLL_INTERVAL_MS = 30000;
          const BATCH_MAX_POLL_DURATION = 3600000; // 1 hour max
          const BATCH_ZERO_PROGRESS_TIMEOUT = 600000; // 10 min with 0 progress → fall back to queue
          const batchStartTime = Date.now();

          while (true) {
            checkAborted();
            await delay(BATCH_POLL_INTERVAL_MS);
            checkAborted();

            const status = await retryOnNetworkError(
              () => getBatchStatus(contractId),
              'getBatchStatus',
            );

            // Handle phantom/stale batch: backend returns batchNotFound when Anthropic 404s
            if (status.batchNotFound || (!status.success && status.error?.includes('not found'))) {
              console.warn('[Orchestrated] Batch not found on Anthropic — falling back to queue extraction');
              batchFailed = true;
              break;
            }

            // Auto-cancel stale batches (>2h with 0 progress, detected by backend)
            if (status.isStale) {
              console.warn(`[Orchestrated] Batch is stale (${status.batchAgeMinutes}min old) — cancelling and falling back`);
              try {
                await cancelBatch(contractId);
              } catch (cancelErr) {
                console.warn('[Orchestrated] Cancel failed (non-fatal):', cancelErr);
              }
              batchFailed = true;
              break;
            }

            const succeeded = status.succeeded || 0;
            const errored = status.errored || 0;
            const expired = status.expired || 0;
            const total = status.total || totalArticles;
            const done = succeeded + errored + expired;
            const progress = Math.round((done / total) * 60) + 15; // 15-75%

            onProgress?.(
              `Extracting articles... ${succeeded}/${total} complete`,
              progress,
              `${errored > 0 ? `${errored} errored` : ''}${expired > 0 ? `, ${expired} expired` : ''}`
            );

            console.log(`[Orchestrated] Batch status: ${status.processingStatus}, ${succeeded}/${total} succeeded, ${errored} errored`);

            if (status.processingStatus === 'ended') {
              break;
            }

            // Zero-progress short-circuit: if no requests have completed after 10 min,
            // the batch is likely stuck on Anthropic's side — fall back to queue immediately
            if (done === 0 && Date.now() - batchStartTime > BATCH_ZERO_PROGRESS_TIMEOUT) {
              console.warn(`[Orchestrated] Batch has 0 progress after ${Math.round((Date.now() - batchStartTime) / 60000)}min — cancelling and falling back to queue`);
              try {
                await cancelBatch(contractId);
              } catch (cancelErr) {
                console.warn('[Orchestrated] Cancel failed (non-fatal):', cancelErr);
              }
              batchFailed = true;
              break;
            }

            if (Date.now() - batchStartTime > BATCH_MAX_POLL_DURATION) {
              throw new Error('Batch extraction timed out after 1 hour');
            }
          }

          // Phase 3c: Retrieve and store batch results (only if batch completed successfully)
          if (!batchFailed) {
            onProgress?.('Storing extraction results...', 78, 'Retrieving batch results');
            let totalStored = 0;
            let totalFailed = 0;
            let nextOffset: number | undefined = 0;

            while (nextOffset !== undefined) {
              checkAborted();
              const resultPage: ProcessBatchResultsResponse = await retryOnNetworkError(
                () => processBatchResults(contractId, nextOffset!),
                `processBatchResults(offset=${nextOffset})`,
              );
              totalStored += resultPage.stored || 0;
              totalFailed += resultPage.failed || 0;
              nextOffset = resultPage.complete ? undefined : resultPage.nextOffset;

              console.log(`[Orchestrated] Batch results page: stored=${resultPage.stored}, failed=${resultPage.failed}, remaining=${resultPage.remaining}`);
            }

            console.log(`[Orchestrated] Batch extraction complete: ${totalStored} stored, ${totalFailed} failed`);
          }
        } else {
          console.warn('[Orchestrated] No articles submitted to batch — skipping poll/results phases');
        }
      }

      // Queue-based fallback: batch submission failed, phantom batch detected, or no batch result
      if (batchFailed) {
        onProgress?.('Extracting articles one at a time (fallback)...', 12, 'Queue fallback');
        let consecutiveErrors = 0;
        const MAX_CONSECUTIVE_ERRORS = 5;

        while (true) {
          checkAborted();
          const result = await processNextArticle(contractId);

          if (!result.success) {
            consecutiveErrors++;
            console.warn(`[Orchestrated] Queue fallback: article failed (${consecutiveErrors} consecutive errors)`);
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              throw new Error(`Queue fallback aborted after ${MAX_CONSECUTIVE_ERRORS} consecutive failures: ${result.error}`);
            }
            continue;
          }

          consecutiveErrors = 0;
          const completed = result.completedArticles || 0;
          const total = result.totalArticles || totalArticles;
          const progress = Math.round((completed / total) * 65) + 12; // 12-77%

          onProgress?.(
            `Extracting articles... ${completed}/${total} complete`,
            progress,
            'Queue-based extraction'
          );

          console.log(`[Orchestrated] Queue fallback: ${completed}/${total}, status=${result.status}`);

          if (result.status === 'completed') {
            break;
          }
        }
      }

      phase3Success = true;
      break; // Phase 3 succeeded, exit retry loop
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      if (phase3Attempt === PHASE3_MAX_RETRIES) {
        throw new Error(`Phase 3 (extraction) failed after ${PHASE3_MAX_RETRIES + 1} attempts: ${err instanceof Error ? err.message : String(err)}`);
      }
      console.error(`[Orchestrated] Phase 3 attempt ${phase3Attempt + 1} failed:`, err);
    }
  }

  if (!phase3Success) {
    throw new Error('Phase 3 (extraction) failed unexpectedly');
  }

  // Phase 4: Merge into complete structure (compact mode — structure stays in R2)
  checkAborted();
  onProgress?.('Finalizing structure...', 90, 'Merging articles');
  let mergeResult;
  try {
    mergeResult = await retryOnNetworkError(
      () => mergeContractStructure(contractId, { compact: true }),
      'mergeContractStructure',
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error(`Phase 4 (merge structure) failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // Phase 5: Store in Supabase using fromR2 (avoids sending large structure over the wire)
  if (shouldStore) {
    checkAborted();
    onProgress?.('Storing structure...', 98);
    try {
      await retryOnNetworkError(
        () => storeStructureFromR2(contractId),
        'storeStructureFromR2',
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new Error(`Phase 5 (store structure) failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  onProgress?.('Complete!', 100);

  // Return a lightweight structure summary (full structure is in R2)
  return {
    contractName: mergeResult.contractName,
    articles: [],
    totalClauses: mergeResult.totalClauses || 0,
    extractionConfidence: 1,
  } as MergedStructure;
}

/**
 * Structure storage response from API
 */
export interface StoreStructureResponse {
  success: boolean;
  documentId: string;
  articlesInserted?: number;
  sectionsInserted?: number;
  clausesInserted?: number;
  status?: string;
  error?: string;
}

/**
 * Store extracted contract structure in Supabase
 */
export async function storeStructure(
  contractId: string,
  structure: ExtractStructureResponse['structure']
): Promise<StoreStructureResponse> {
  console.log('[API] Storing structure for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/store-structure`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ structure }),
  });

  const data = await response.json();
  console.log('[API] Store structure response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to store structure');
  }

  return data;
}

/**
 * Store structure in Supabase by loading from R2 (avoids large client upload)
 */
export async function storeStructureFromR2(contractId: string): Promise<StoreStructureResponse> {
  console.log('[API] Storing structure from R2 for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/store-structure`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fromR2: true }),
  });

  const data = await response.json();
  console.log('[API] Store structure (fromR2) response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to store structure from R2');
  }

  return data;
}

/**
 * Response from embedding pipeline
 */
export interface EmbedClausesResponse {
  success: boolean;
  documentId: string;
  totalClauses: number;
  embeddedClauses: number;
  failedClauses: number;
  totalTokensUsed: number;
  processingTime: number;
  status?: string;
  error?: string;
}

/**
 * Generate embeddings for all clauses in a contract
 * This enables semantic clause matching and similarity search
 */
export async function embedClauses(contractId: string): Promise<EmbedClausesResponse> {
  console.log('[API] Generating embeddings for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/embed-clauses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      onlyMissingEmbeddings: true,
      updateContractStatus: true,
      storeMetadata: true,
    }),
  });

  const data = await response.json();
  console.log('[API] Embed clauses response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to generate embeddings');
  }

  return data;
}

/**
 * Response from chunked embedding endpoint
 */
export interface EmbedClausesChunkResponse {
  success: boolean;
  contractId: string;
  offset: number;
  processed: number;
  remaining: number;
  totalClauses: number;
  complete: boolean;
  embeddedClauses: number;
  failedClauses: number;
  totalTokensUsed: number;
  pineconeUpserted?: number;
  pineconeError?: string;
  error?: string;
}

/**
 * Embed a single chunk of clauses (200 at a time by default)
 */
export async function embedClausesChunk(
  contractId: string,
  offset: number = 0,
  limit: number = 200
): Promise<EmbedClausesChunkResponse> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/documents/${contractId}/embed-clauses-chunk?offset=${offset}&limit=${limit}`,
    { method: 'POST' }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || `Failed to embed clauses chunk at offset ${offset}`);
  }

  return data;
}

/**
 * Progress callback for chunked embedding
 */
export type EmbeddingProgressCallback = (embedded: number, total: number) => void;

/**
 * Drive the chunked embedding loop until all clauses are embedded.
 * Retries each chunk up to 3 times on failure.
 * Returns { complete: false, error } on chunk failure instead of throwing,
 * so callers can handle partial progress. AbortError is still thrown.
 */
export async function embedClausesChunked(
  contractId: string,
  onProgress?: EmbeddingProgressCallback,
  signal?: AbortSignal
): Promise<{ totalEmbedded: number; totalClauses: number; complete: boolean; error?: string }> {
  const CHUNK_SIZE = 200;
  const MAX_CHUNK_RETRIES = 3;
  let totalEmbedded = 0;
  let initialTotal = 0;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Embedding aborted', 'AbortError');
    }

    let lastError: Error | undefined;
    let result: EmbedClausesChunkResponse | undefined;

    for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt++) {
      try {
        // Always offset=0: the list of unembedded clauses shrinks after each chunk
        result = await embedClausesChunk(contractId, 0, CHUNK_SIZE);
        lastError = undefined;
        break;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        console.warn(`[API] Embed chunk attempt ${attempt + 1} failed:`, lastError.message);
        if (attempt < MAX_CHUNK_RETRIES - 1) {
          await delay(2000 * Math.pow(2, attempt));
        }
      }
    }

    if (lastError || !result) {
      const errorMsg = lastError?.message || 'Embedding chunk failed';
      console.error(`[API] embedClausesChunked stopping after ${totalEmbedded} clauses:`, errorMsg);
      return { totalEmbedded, totalClauses: initialTotal, complete: false, error: errorMsg };
    }

    totalEmbedded += result.embeddedClauses;
    // Capture the initial total on first call (totalClauses = remaining unembedded)
    if (initialTotal === 0 && result.totalClauses > 0) {
      initialTotal = result.totalClauses;
    }
    onProgress?.(totalEmbedded, initialTotal || result.totalClauses);

    if (result.complete) {
      break;
    }
  }

  return { totalEmbedded, totalClauses: initialTotal, complete: true };
}

/**
 * Clause record from API
 */
export interface ClauseFromAPI {
  id: string;
  section_id: string;
  number: string;
  text: string;
  metadata?: {
    cfr_citation?: string;
    has_risk?: boolean;
    risk_severity?: 'critical' | 'high' | 'medium' | 'low';
    [key: string]: unknown;
  } | null;
  created_at: string;
  updated_at: string;
}

/**
 * Section record with clauses
 */
export interface SectionWithClauses {
  id: string;
  article_id: string;
  number: string;
  title: string;
  content_hash: string | null;
  created_at: string;
  updated_at: string;
  clauses?: ClauseFromAPI[];
}

/**
 * Article with nested sections from API
 */
export interface ArticleFromAPI {
  id: string;
  number: string;
  title: string;
  summary: string | null;
  created_at: string;
  updated_at: string;
  sections: SectionWithClauses[];
}

/**
 * Analysis result summary from API
 */
export interface AnalysisResultFromAPI {
  id: string;
  analysis_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  gap_count: number;
  results_summary: {
    totalGaps?: number;
    gapsByType?: Record<string, number>;
    gapsBySeverity?: Record<string, number>;
    processingTime?: number;
  } | null;
}

/**
 * Contract detail response from API
 */
export interface ContractDetailResponse {
  success: boolean;
  error?: string;
  contract: ContractFromAPI;
  articles: ArticleFromAPI[];
  analysisResults: AnalysisResultFromAPI[];
  statistics: {
    totalArticles: number;
    totalSections: number;
    totalClauses: number;
  };
}

/**
 * Fetch detailed contract information including structure and analysis results
 */
export async function fetchContractDetail(contractId: string): Promise<ContractDetailResponse> {
  console.log('[API] Fetching contract detail:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/contracts/${contractId}`);
  const data = await response.json();
  console.log('[API] Contract detail response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch contract detail');
  }

  return data;
}

/**
 * Gap finding from backend API
 */
export interface GapFindingFromAPI {
  id: string;
  analysis_id: string;
  source_clause_id: string | null;
  target_clause_id: string | null;
  gap_type: 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated' | 'compliance_risk';
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  recommendation: string;
  created_at: string;
  source_clause_text?: string | null;
  target_clause_text?: string | null;
  source_article_number?: string | null;
  source_section_number?: string | null;
  target_article_number?: string | null;
  target_section_number?: string | null;
}

/**
 * Gap findings list response from API
 */
export interface GapFindingsListResponse {
  success: boolean;
  data: GapFindingFromAPI[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  statistics: {
    totalGaps: number;
    gapsByType: Record<string, number>;
    gapsBySeverity: Record<string, number>;
  };
  error?: string;
}

/**
 * Parameters for fetching gap findings
 */
export interface FetchGapFindingsParams {
  analysisId?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  gapType?: 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated' | 'compliance_risk';
  page?: number;
  limit?: number;
}

/**
 * Fetch gap findings from the API with optional filtering and pagination
 */
export async function fetchGapFindings(params: FetchGapFindingsParams = {}): Promise<GapFindingsListResponse> {
  const searchParams = new URLSearchParams();

  if (params.analysisId) searchParams.set('analysisId', params.analysisId);
  if (params.severity) searchParams.set('severity', params.severity);
  if (params.gapType) searchParams.set('gapType', params.gapType);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/gaps${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching gap findings from:', url);

  const response = await apiFetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch gap findings' }));
    console.error('[API] Fetch gap findings failed:', error);
    throw new Error(error.error || 'Failed to fetch gap findings');
  }

  const data: GapFindingsListResponse = await response.json();
  console.log('[API] Gap findings response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

// ============================================================
// Gap Analysis with AI API
// ============================================================

/**
 * Response type for gap analysis with AI
 */
export interface GapAnalysisResponse {
  success: boolean;
  analysisId: string;
  totalGaps: number;
  gapsByType: Record<string, number>;
  gapsBySeverity: Record<string, number>;
  error?: string;
}

/**
 * Request type for gap analysis with AI
 */
export interface AnalyzeGapsRequest {
  sourceContractId: string;
  targetContractId: string;
  similarityThreshold?: number;
  maxPairs?: number;
}

/**
 * Analyze gaps between two contracts using Claude AI
 * This calls the backend to:
 * 1. Find similar clause pairs using vector embeddings
 * 2. Send pairs to Claude AI for gap identification
 * 3. Store gap findings in the database
 */
export async function analyzeGapsWithAI(params: AnalyzeGapsRequest): Promise<GapAnalysisResponse> {
  console.log('[API] Analyzing gaps with AI:', params.sourceContractId, 'vs', params.targetContractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analyze/gaps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceContractId: params.sourceContractId,
      targetContractId: params.targetContractId,
      similarityThreshold: params.similarityThreshold ?? 0.5,
      maxPairs: params.maxPairs ?? 100,
    }),
  });

  const data = await response.json();
  console.log('[API] Gap analysis response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to analyze gaps');
  }

  return data;
}

/**
 * Transform API gap finding to frontend GapFinding format
 */
export function transformGapFinding(apiFinding: GapFindingFromAPI): {
  id: string;
  gap_type: 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated';
  severity: 'critical' | 'high' | 'medium' | 'low';
  source_clause_id: string;
  source_clause_text: string;
  source_clause_reference: string;
  target_clause_id?: string;
  target_clause_text?: string;
  target_clause_reference?: string;
  description: string;
  recommendation: string;
  cfr_citation?: string;
  created_at: string;
} {
  // Build clause reference strings from article/section numbers
  // Article and section numbers are stored as strings like "1", "1.1", "1.1.8.4"
  const formatClauseReference = (
    articleNum: string | null | undefined,
    sectionNum: string | null | undefined,
    clauseId: string | null | undefined
  ): string => {
    if (!articleNum && !sectionNum) return '';
    const parts: string[] = [];
    if (articleNum) parts.push(`Article ${articleNum}`);
    if (sectionNum) parts.push(`Section ${sectionNum}`);
    return parts.join(', ');
  };

  // Map compliance_risk to ambiguous for frontend compatibility
  const gapType = apiFinding.gap_type === 'compliance_risk'
    ? 'ambiguous'
    : apiFinding.gap_type as 'missing_requirement' | 'weaker_language' | 'ambiguous' | 'outdated';

  return {
    id: apiFinding.id,
    gap_type: gapType,
    severity: apiFinding.severity,
    source_clause_id: apiFinding.source_clause_id || '',
    source_clause_text: apiFinding.source_clause_text || '',
    source_clause_reference: formatClauseReference(
      apiFinding.source_article_number,
      apiFinding.source_section_number,
      apiFinding.source_clause_id
    ),
    target_clause_id: apiFinding.target_clause_id || undefined,
    target_clause_text: apiFinding.target_clause_text || undefined,
    target_clause_reference: apiFinding.target_clause_id
      ? formatClauseReference(
          apiFinding.target_article_number,
          apiFinding.target_section_number,
          apiFinding.target_clause_id
        )
      : undefined,
    description: apiFinding.description,
    recommendation: apiFinding.recommendation,
    created_at: apiFinding.created_at,
  };
}

// ============================================================
// Risk Analysis API
// ============================================================

/**
 * Risk severity type
 */
export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low';

/**
 * Risk category type
 */
export type RiskCategory =
  | 'regulatory_compliance'
  | 'network_adequacy'
  | 'member_rights'
  | 'care_management'
  | 'quality_oversight'
  | 'financial_compliance'
  | 'data_reporting'
  | 'operational';

/**
 * Risk finding from backend API
 */
export interface RiskFindingFromAPI {
  id: string;
  clauseId: string;
  clauseText: string;
  severity: RiskSeverity;
  category: RiskCategory;
  description: string;
  mitigation: string;
  cfrCitation?: string;
  confidence: number;
  created_at?: string;
}

/**
 * Contract risks response from API
 */
export interface ContractRisksResponse {
  success: boolean;
  documentId: string;
  risks: RiskFindingFromAPI[];
  totalRisks: number;
  risksBySeverity: Record<RiskSeverity, number>;
  risksByCategory: Record<RiskCategory, number>;
  error?: string;
}

/**
 * Article risk score from backend API
 */
export interface ArticleRiskScoreFromAPI {
  articleId: string;
  articleNumber: string;
  articleTitle: string;
  totalRisks: number;
  risksBySeverity: Record<RiskSeverity, number>;
  risksByCategory: Record<RiskCategory, number>;
  aggregateScore: number;
  maxSeverity: RiskSeverity | null;
}

/**
 * Risk scores response from API
 */
export interface RiskScoresResponse {
  success: boolean;
  contractId: string;
  totalRisks: number;
  overallScore: number;
  risksBySeverity: Record<RiskSeverity, number>;
  risksByCategory: Record<RiskCategory, number>;
  articleScores: ArticleRiskScoreFromAPI[];
  severityWeights: Record<RiskSeverity, number>;
  error?: string;
}

/**
 * Fetch contract risks from the API
 */
export async function fetchContractRisks(contractId: string): Promise<ContractRisksResponse> {
  console.log('[API] Fetching contract risks:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/risks`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch contract risks' }));
    console.error('[API] Fetch contract risks failed:', error);
    throw new Error(error.error || 'Failed to fetch contract risks');
  }

  const data: ContractRisksResponse = await response.json();
  console.log('[API] Contract risks response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Fetch risk scores per article from the API
 */
export async function fetchRiskScores(contractId: string): Promise<RiskScoresResponse> {
  console.log('[API] Fetching risk scores:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/risk-scores`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch risk scores' }));
    console.error('[API] Fetch risk scores failed:', error);
    throw new Error(error.error || 'Failed to fetch risk scores');
  }

  const data: RiskScoresResponse = await response.json();
  console.log('[API] Risk scores response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Transform API risk finding to frontend RiskFinding format (for Priority Matrix)
 */
export function transformRiskFinding(
  apiRisk: RiskFindingFromAPI,
  contractId: string
): {
  id: string;
  description: string;
  severity: RiskSeverity;
  impact: number;
  likelihood: number;
  contractId: string;
  articleId?: string;
  articleNumber?: string;
  articleTitle?: string;
  cfrCitation?: string;
  clauseReference?: string;
  recommendation?: string;
} {
  // Calculate impact and likelihood from severity and confidence
  // Severity maps to impact: critical=9, high=7, medium=5, low=3
  const severityToImpact: Record<RiskSeverity, number> = {
    critical: 9,
    high: 7,
    medium: 5,
    low: 3,
  };

  // Confidence maps to likelihood (0-1 → 1-10)
  const likelihood = Math.round(apiRisk.confidence * 10);

  return {
    id: apiRisk.id,
    description: apiRisk.description,
    severity: apiRisk.severity,
    impact: severityToImpact[apiRisk.severity],
    likelihood: Math.max(1, Math.min(10, likelihood)), // Clamp to 1-10
    contractId,
    cfrCitation: apiRisk.cfrCitation,
    clauseReference: apiRisk.clauseId, // Use clauseId as reference
    recommendation: apiRisk.mitigation,
  };
}

// ============================================================
// Dashboard Analytics API
// ============================================================

/**
 * Dashboard analytics response from API
 */
export interface DashboardAnalyticsResponse {
  success: boolean;
  error?: string;
  healthScore: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
  gapsBySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  totalGaps: number;
  totalContracts: number;
  totalAnalyses: number;
  analysesThisWeek: number;
  lastUpdated: string;
}

/**
 * Fetch dashboard analytics data
 */
export async function fetchDashboardAnalytics(): Promise<DashboardAnalyticsResponse> {
  console.log('[API] Fetching dashboard analytics');

  const response = await apiFetch(`${API_BASE_URL}/api/analytics/dashboard`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch dashboard analytics' }));
    console.error('[API] Dashboard analytics failed:', error);
    throw new Error(error.error || 'Failed to fetch dashboard analytics');
  }

  const data: DashboardAnalyticsResponse = await response.json();
  console.log('[API] Dashboard analytics response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

// ============================================================
// Semantic Search API
// ============================================================

/**
 * Search result from backend API
 */
export interface SearchResultFromAPI {
  id: string;
  text: string;
  contractId?: string;
  contractState?: string;
  articleNumber?: string;
  sectionNumber?: string;
  similarity: number;
}

/**
 * Semantic search response from API
 */
export interface SemanticSearchResponse {
  success: boolean;
  error?: string;
  message?: string;
  query: string;
  results: SearchResultFromAPI[];
  totalResults: number;
  cached: boolean;
  processingTime?: number;
}

/**
 * Parameters for semantic search
 */
export interface SemanticSearchParams {
  query: string;
  state?: string;
  matchCount?: number;
  similarityThreshold?: number;
}

/**
 * Perform semantic search across contracts
 */
export async function semanticSearch(params: SemanticSearchParams): Promise<SemanticSearchResponse> {
  console.log('[API] Performing semantic search:', params.query);

  const response = await apiFetch(`${API_BASE_URL}/api/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: params.query,
      state: params.state,
      matchCount: params.matchCount ?? 20,
      similarityThreshold: params.similarityThreshold ?? 0.5,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to perform search' }));
    console.error('[API] Semantic search failed:', error);
    throw new Error(error.error || 'Failed to perform search');
  }

  const data: SemanticSearchResponse = await response.json();
  console.log('[API] Semantic search response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Transform API search result to frontend SearchResult format
 */
export function transformSearchResult(
  apiResult: SearchResultFromAPI,
  query: string
): {
  id: string;
  text: string;
  contractId: string;
  contractName: string;
  contractState: string;
  articleNumber: string;
  articleTitle: string;
  sectionNumber: string;
  sectionTitle: string;
  clauseNumber: string;
  similarity: number;
  highlights: { start: number; end: number }[];
  cfrCitation?: string;
} {
  // Generate highlights based on query words
  const words = query.toLowerCase().split(' ').filter((w) => w.length > 2);
  const highlights: { start: number; end: number }[] = [];
  const lowerText = apiResult.text.toLowerCase();

  words.forEach((word) => {
    let index = lowerText.indexOf(word);
    while (index !== -1) {
      highlights.push({ start: index, end: index + word.length });
      index = lowerText.indexOf(word, index + 1);
    }
  });

  // Extract CFR citation if present in text
  const cfrMatch = apiResult.text.match(/42\s*CFR\s*\d+(\.\d+)?/i);

  return {
    id: apiResult.id,
    text: apiResult.text,
    contractId: apiResult.contractId || '',
    contractName: `Contract (${apiResult.contractState || 'Unknown'})`, // Contract name not in API, use state
    contractState: apiResult.contractState || '',
    articleNumber: apiResult.articleNumber || '',
    articleTitle: `Article ${apiResult.articleNumber || ''}`, // Title not in API
    sectionNumber: apiResult.sectionNumber || '',
    sectionTitle: `Section ${apiResult.sectionNumber || ''}`, // Title not in API
    clauseNumber: '', // Clause number not in API
    similarity: apiResult.similarity,
    highlights,
    cfrCitation: cfrMatch ? cfrMatch[0] : undefined,
  };
}

// ============================================================
// Contract Comparison API
// ============================================================

/**
 * Similar clause pair from comparison API
 */
export interface SimilarClausePairFromAPI {
  sourceClauseId: string;
  sourceText: string;
  sourceContractId: string;
  sourceState: string;
  targetClauseId: string;
  targetText: string;
  targetContractId: string;
  targetState: string;
  similarity: number;
}

/**
 * Structural difference from comparison API
 */
export interface StructuralDifferenceFromAPI {
  type: 'missing_article' | 'missing_section' | 'extra_article' | 'extra_section';
  contractId: string;
  articleNumber?: string;
  articleTitle?: string;
  sectionNumber?: string;
  sectionTitle?: string;
}

/**
 * Article structure from comparison API
 */
export interface ArticleStructureFromAPI {
  number: string;
  title: string;
  sectionCount: number;
  clauseCount: number;
}

/**
 * Unmatched clause from comparison API
 * Represents a clause that has no good match in the other contract
 */
export interface UnmatchedClause {
  clauseId: string;
  clauseText: string;
  contractId: string;
  contractState: string;
  articleNumber: string;
  sectionNumber: string;
  bestMatchSimilarity: number;
}

/**
 * Embedding statistics from comparison API
 */
export interface EmbeddingStatsFromAPI {
  sourceClausesTotal: number;
  sourceClausesWithEmbeddings: number;
  targetClausesTotal: number;
  targetClausesWithEmbeddings: number;
  warning?: string;
}

/**
 * Comparison response from API
 */
export interface CompareContractsResponse {
  success: boolean;
  error?: string;
  message?: string;
  sourceContractId: string;
  targetContractId: string;
  semantic: {
    similarClauses: SimilarClausePairFromAPI[];
    totalSimilarPairs: number;
    averageSimilarity: number;
  };
  structural?: {
    sourceArticleCount: number;
    targetArticleCount: number;
    sourceArticles: ArticleStructureFromAPI[];
    targetArticles: ArticleStructureFromAPI[];
    differences: StructuralDifferenceFromAPI[];
  } | null;
  unmatchedClauses?: {
    sourceUnmatched: UnmatchedClause[];
    targetUnmatched: UnmatchedClause[];
    totalSourceUnmatched: number;
    totalTargetUnmatched: number;
  } | null;
  embeddingStats?: EmbeddingStatsFromAPI | null;
  processingTime?: number;
  options?: {
    similarityThreshold: number;
    maxSimilarPairs: number;
    structuralComparisonEnabled: boolean;
    neo4jConfigured: boolean;
  };
}

/**
 * Parameters for comparing contracts
 */
export interface CompareContractsParams {
  sourceContractId: string;
  targetContractId: string;
  similarityThreshold?: number;
  maxSimilarPairs?: number;
  includeStructural?: boolean;
}

/**
 * Compare two contracts using semantic similarity and structural analysis
 */
export async function compareContracts(params: CompareContractsParams): Promise<CompareContractsResponse> {
  console.log('[API] Comparing contracts:', params.sourceContractId, 'vs', params.targetContractId);

  const response = await apiFetch(`${API_BASE_URL}/api/compare`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sourceContractId: params.sourceContractId,
      targetContractId: params.targetContractId,
      similarityThreshold: params.similarityThreshold ?? 0.7,
      maxSimilarPairs: params.maxSimilarPairs ?? 100,
      includeStructural: params.includeStructural ?? true,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to compare contracts' }));
    console.error('[API] Compare contracts failed:', error);
    throw new Error(error.error || 'Failed to compare contracts');
  }

  const data: CompareContractsResponse = await response.json();
  console.log('[API] Compare contracts response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}


export interface RetrievalRerankCandidate {
  chunkId: string;
  score: number;
  rerankScore: number;
  sectionPath?: string;
  sectionId?: string;
  page?: number;
  text?: string;
}

export interface RetrievalRerankTelemetry {
  recallAtK?: number;
  rerankLift: number;
  baselineTopScore: number;
  rerankedTopScore: number;
}

export interface RetrievalRerankResponse {
  success: boolean;
  error?: string;
  queryText: string;
  targetDocId: string;
  candidates: RetrievalRerankCandidate[];
  telemetry: RetrievalRerankTelemetry;
}

export async function retrieveAndRerankCompareCandidates(params: {
  queryText: string;
  targetDocId: string;
  topK?: number;
  expectedChunkIds?: string[];
}): Promise<RetrievalRerankResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/compare/retrieve-rerank`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to retrieve reranked candidates' }));
    throw new Error(error.error || 'Failed to retrieve reranked candidates');
  }

  const data: RetrievalRerankResponse = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }
  return data;
}

/**
 * Transform comparison API result to ClauseMatch format for SideBySideViewer
 */
export function transformSimilarClausePair(
  pair: SimilarClausePairFromAPI
): {
  sourceClauseId: string;
  targetClauseId: string;
  similarity: number;
  matchType: 'exact' | 'similar' | 'partial' | 'different';
} {
  // Determine match type based on similarity
  let matchType: 'exact' | 'similar' | 'partial' | 'different';
  if (pair.similarity >= 0.9) {
    matchType = 'exact';
  } else if (pair.similarity >= 0.7) {
    matchType = 'similar';
  } else if (pair.similarity >= 0.5) {
    matchType = 'partial';
  } else {
    matchType = 'different';
  }

  return {
    sourceClauseId: pair.sourceClauseId,
    targetClauseId: pair.targetClauseId,
    similarity: pair.similarity,
    matchType,
  };
}

// ============================================================
// Risks by Article API (for Heat Map)
// ============================================================

/**
 * Heat map risk item from backend
 */
export interface HeatMapRiskItemFromAPI {
  id: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  cfrCitation?: string;
  clauseReference?: string;
}

/**
 * Heat map article risk from backend
 */
export interface HeatMapArticleRiskFromAPI {
  articleId: string;
  articleNumber: string;
  articleTitle: string;
  risks: HeatMapRiskItemFromAPI[];
  riskCounts: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  overallSeverity: 'critical' | 'high' | 'medium' | 'low' | 'none';
}

/**
 * Risks by article response from API
 */
export interface RisksByArticleResponse {
  success: boolean;
  error?: string;
  contractId: string;
  totalRisks: number;
  articles: HeatMapArticleRiskFromAPI[];
}

/**
 * Fetch risks grouped by article for heat map visualization
 */
export async function fetchRisksByArticle(contractId: string): Promise<RisksByArticleResponse> {
  console.log('[API] Fetching risks by article:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/risks/by-article`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch risks by article' }));
    console.error('[API] Fetch risks by article failed:', error);
    throw new Error(error.error || 'Failed to fetch risks by article');
  }

  const data: RisksByArticleResponse = await response.json();
  console.log('[API] Risks by article response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

// ============================================================
// Recent Activity API
// ============================================================

/**
 * Activity type from backend
 */
export type ActivityType = 'upload' | 'analysis' | 'gap_analysis' | 'risk_analysis' | 'report' | 'processing';

/**
 * Activity status type
 */
export type ActivityStatus = 'completed' | 'in_progress' | 'failed';

/**
 * Activity item from backend API
 */
export interface ActivityItemFromAPI {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  status: ActivityStatus;
  contractName?: string;
}

/**
 * Recent activity response from API
 */
export interface RecentActivityResponse {
  success: boolean;
  error?: string;
  activities: ActivityItemFromAPI[];
  total: number;
  hasMore: boolean;
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

/**
 * Parameters for fetching recent activity
 */
export interface FetchRecentActivityParams {
  limit?: number;
  offset?: number;
  type?: ActivityType;
  status?: ActivityStatus;
  contractId?: string;
}

/**
 * Fetch recent activities for the dashboard
 */
export async function fetchRecentActivity(params: FetchRecentActivityParams = {}): Promise<RecentActivityResponse> {
  const searchParams = new URLSearchParams();

  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());
  if (params.type) searchParams.set('type', params.type);
  if (params.status) searchParams.set('status', params.status);
  if (params.contractId) searchParams.set('contractId', params.contractId);

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/activity/recent${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching recent activity from:', url);

  const response = await apiFetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch recent activity' }));
    console.error('[API] Fetch recent activity failed:', error);
    throw new Error(error.error || 'Failed to fetch recent activity');
  }

  const data: RecentActivityResponse = await response.json();
  console.log('[API] Recent activity response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Transform API article risk scores to frontend ArticleRisk format (for Heat Map)
 */
export function transformArticleRiskScore(
  apiScore: ArticleRiskScoreFromAPI,
  contractRisks: RiskFindingFromAPI[]
): {
  articleId: string;
  articleNumber: string;
  articleTitle: string;
  risks: Array<{
    id: string;
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'none';
    cfrCitation?: string;
    clauseReference?: string;
  }>;
  riskCounts: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  overallSeverity: 'critical' | 'high' | 'medium' | 'low' | 'none';
} {
  // Filter risks for this article (we don't have article info in risks, so return empty for now)
  // In a real implementation, we'd need to get risks per article from the backend
  const articleRisks: Array<{
    id: string;
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'none';
    cfrCitation?: string;
    clauseReference?: string;
  }> = [];

  // Map max severity to overall severity
  const overallSeverity: 'critical' | 'high' | 'medium' | 'low' | 'none' =
    apiScore.maxSeverity || 'none';

  return {
    articleId: apiScore.articleId,
    articleNumber: apiScore.articleNumber,
    articleTitle: apiScore.articleTitle || `Article ${apiScore.articleNumber}`,
    risks: articleRisks,
    riskCounts: {
      critical: apiScore.risksBySeverity.critical || 0,
      high: apiScore.risksBySeverity.high || 0,
      medium: apiScore.risksBySeverity.medium || 0,
      low: apiScore.risksBySeverity.low || 0,
    },
    overallSeverity,
  };
}

// ============================================================
// Compliance Analytics API (Regulatory Alignment Radar)
// ============================================================

/**
 * Compliance gap from backend API
 */
export interface ComplianceGapFromAPI {
  id: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  recommendation?: string;
}

/**
 * Compliance scores response from API
 */
export interface ComplianceScoresResponse {
  success: boolean;
  error?: string;
  contractId: string;
  contractName: string;
  state: string;
  scores: Record<string, number>;
  gaps: Record<string, ComplianceGapFromAPI[]>;
  lastUpdated: string;
}

/**
 * Fetch compliance scores for a contract (for Regulatory Alignment Radar)
 */
export async function fetchComplianceScores(contractId: string): Promise<ComplianceScoresResponse> {
  console.log('[API] Fetching compliance scores:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analytics/compliance/${contractId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch compliance scores' }));
    console.error('[API] Fetch compliance scores failed:', error);
    throw new Error(error.error || 'Failed to fetch compliance scores');
  }

  const data: ComplianceScoresResponse = await response.json();
  console.log('[API] Compliance scores response:', data);

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Transform API compliance scores to ContractComplianceData format for RegulatoryAlignmentRadar
 */
export function transformComplianceScores(
  apiResponse: ComplianceScoresResponse,
  color: string = '#3b82f6'
): {
  contractId: string;
  contractName: string;
  state: string;
  color: string;
  scores: Record<string, number>;
  gaps: Record<string, Array<{ id: string; description: string; severity: 'critical' | 'high' | 'medium' | 'low'; recommendation?: string }>>;
} {
  return {
    contractId: apiResponse.contractId,
    contractName: apiResponse.contractName,
    state: apiResponse.state,
    color,
    scores: apiResponse.scores,
    gaps: apiResponse.gaps,
  };
}

// ============================================================
// LLM-Based Article/Clause Matching API
// ============================================================

/**
 * TOC entry for LLM article/clause matching (distinct from the flexible TOCEntry above)
 */
export interface LLMTOCEntry {
  articleNumber: string;
  articleTitle: string;
  sectionCount: number;
  sections?: { number: string; title: string }[];
}

/**
 * Match result between a baseline and target article
 */
export interface ArticleMatch {
  baselineArticleNumber: string;
  baselineArticleTitle: string;
  targetArticleNumber: string | null;
  targetArticleTitle: string | null;
  confidence: number;
  matchReason: string;
}

/**
 * Section match within an article pair
 */
export interface SectionMatch {
  baselineSectionNumber: string;
  baselineSectionTitle: string;
  targetSectionNumber: string | null;
  targetSectionTitle: string | null;
  confidence: number;
  matchReason: string;
  keyDifferences?: string[];
}

/**
 * Section matching results for an article pair
 */
export interface ArticleSectionMatches {
  baselineArticleNumber: string;
  targetArticleNumber: string;
  sectionMatches: SectionMatch[];
  unmatchedBaselineSections: { number: string; title: string }[];
  unmatchedTargetSections: { number: string; title: string }[];
}

/**
 * LLM matching session status
 */
export type LLMMatchingStatus =
  | 'pending'
  | 'toc_matching'
  | 'awaiting_review'
  | 'section_matching'
  | 'completed'
  | 'failed';

/**
 * LLM matching session from API
 */
export interface LLMMatchingSession {
  id: string;
  baseline_contract_id: string;
  target_contract_id: string;
  status: LLMMatchingStatus;
  toc_matches: {
    matches: ArticleMatch[];
    unmatchedBaseline: LLMTOCEntry[];
    unmatchedTarget: LLMTOCEntry[];
  } | null;
  approved_mappings: {
    articlePairs: Array<{
      baselineNumber: string;
      targetNumber: string | null;
      approved: boolean;
    }>;
  } | null;
  section_matches: {
    articlePairs: ArticleSectionMatches[];
  } | null;
  total_input_tokens: number;
  total_output_tokens: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * TOC matching request parameters
 */
export interface TOCMatchingParams {
  baselineContractId: string;
  targetContractId: string;
  includeSectionTitles?: boolean;
}

/**
 * TOC matching response from API
 */
export interface TOCMatchingResponse {
  success: boolean;
  error?: string;
  sessionId?: string;
  matches: ArticleMatch[];
  unmatchedBaseline: LLMTOCEntry[];
  unmatchedTarget: LLMTOCEntry[];
  tokensUsed: { input: number; output: number };
}

/**
 * Start TOC matching between two contracts (Phase 1)
 */
export async function startTOCMatching(params: TOCMatchingParams): Promise<TOCMatchingResponse> {
  console.log('[API] Starting TOC matching:', params.baselineContractId, 'vs', params.targetContractId);

  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/toc-match`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      baselineContractId: params.baselineContractId,
      targetContractId: params.targetContractId,
      includeSectionTitles: params.includeSectionTitles ?? false,
    }),
  });

  const data = await response.json();
  console.log('[API] TOC matching response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to start TOC matching');
  }

  return data;
}

/**
 * Get LLM matching session response
 */
export interface GetLLMSessionResponse {
  success: boolean;
  error?: string;
  session?: LLMMatchingSession;
}

/**
 * Get LLM matching session by ID
 */
export async function getLLMMatchingSession(sessionId: string): Promise<GetLLMSessionResponse> {
  console.log('[API] Getting LLM matching session:', sessionId);

  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/session/${sessionId}`);
  const data = await response.json();
  console.log('[API] LLM session response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get session');
  }

  return data;
}

/**
 * Article mapping approval
 */
export interface ArticleMappingApproval {
  baselineNumber: string;
  targetNumber: string | null;
  approved: boolean;
}

/**
 * Approve article mappings response
 */
export interface ApproveArticleMappingsResponse {
  success: boolean;
  error?: string;
  sessionId?: string;
  message?: string;
}

/**
 * Approve article mappings for a session (Phase 1b)
 */
export async function approveArticleMappings(
  sessionId: string,
  mappings: ArticleMappingApproval[]
): Promise<ApproveArticleMappingsResponse> {
  console.log('[API] Approving article mappings for session:', sessionId);

  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/session/${sessionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      approvedMappings: mappings,
    }),
  });

  const data = await response.json();
  console.log('[API] Approve mappings response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to approve mappings');
  }

  return data;
}

/**
 * Section matching request parameters
 */
export interface SectionMatchingParams {
  sessionId: string;
  articlePairs?: Array<{ baseline: string; target: string }>;
}

/**
 * Section matching response from API
 */
export interface SectionMatchingResponse {
  success: boolean;
  error?: string;
  sessionId?: string;
  sectionMatches: ArticleSectionMatches[];
  articlesProcessed: number;
  skippedPairs?: Array<{ baseline: string; target: string; reason: string }>;
  tokensUsed: { input: number; output: number };
}

/**
 * Start section matching for approved article pairs (Phase 2)
 */
export async function startSectionMatching(params: SectionMatchingParams): Promise<SectionMatchingResponse> {
  console.log('[API] Starting section matching for session:', params.sessionId);

  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/section-match`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionId: params.sessionId,
      articlePairs: params.articlePairs,
    }),
  });

  const data = await response.json();
  console.log('[API] Section matching response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to start section matching');
  }

  return data;
}

// ============================================================
// LLM Matching Session List & Delete
// ============================================================

/**
 * Summary of an LLM matching session (no large JSONB payloads)
 */
export interface LLMSessionSummary {
  id: string;
  baselineContractId: string;
  targetContractId: string;
  status: LLMMatchingStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * List LLM matching sessions for the current user
 */
export async function listLLMSessions(options?: {
  limit?: number;
  offset?: number;
}): Promise<{ sessions: LLMSessionSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();

  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/sessions${qs ? '?' + qs : ''}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to list LLM sessions');
  }
  return { sessions: data.sessions, total: data.total };
}

/**
 * Delete an LLM matching session by ID
 */
export async function deleteLLMSession(sessionId: string): Promise<void> {
  const response = await apiFetch(`${API_BASE_URL}/api/compare/llm/session/${sessionId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to delete session');
  }
}

/**
 * Get confidence level for display styling
 */
export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.85) return 'high';
  if (confidence >= 0.7) return 'medium';
  return 'low';
}

/**
 * Get confidence badge color class
 */
export function getConfidenceBadgeColor(confidence: number): string {
  const level = getConfidenceLevel(confidence);
  switch (level) {
    case 'high':
      return 'bg-green-100 text-green-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
      return 'bg-red-100 text-red-800';
  }
}

export interface TopicSummaryFromAPI {
  topic: string;
  chunkCount: number;
  contractCount: number;
  states: string[];
}

export interface DefinedTermFromAPI {
  term: string;
  normalized: string;
  chunkCount: number;
  contractCount: number;
}

export interface TopicComparisonContractFromAPI {
  contractId: string;
  contractName: string;
  state: string;
  chunkCount: number;
  topSections: string[];
}

export async function fetchTopicSummaries(limit = 20): Promise<TopicSummaryFromAPI[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/topics?limit=${limit}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to load topics');
  }
  return data.topics || [];
}

export async function fetchDefinedTerms(limit = 30): Promise<DefinedTermFromAPI[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/terms?limit=${limit}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to load defined terms');
  }
  return data.terms || [];
}

export async function fetchTopicComparison(topic: string): Promise<TopicComparisonContractFromAPI[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/topics/${encodeURIComponent(topic)}/compare-states`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to load topic comparison');
  }
  return data.contracts || [];
}

// ---------------------------------------------------------------------------
// Financial Risk Analysis
// ---------------------------------------------------------------------------

export interface FinancialRiskFinding {
  id: string;
  clauseId: string;
  clauseText: string;
  articleNumber: string;
  sectionNumber: string;
  sectionTitle: string;
  ruleId: string;
  ruleName: string;
  flagType: string;
  severity: string;
  matchText: string;
  charOffsetStart: number;
  charOffsetEnd: number;
  rationale: string;
}

export interface FinancialRiskAnalysisResponse {
  success: boolean;
  analysisId: string;
  findings: FinancialRiskFinding[];
  totalFindings: number;
  findingsByFlagType?: Record<string, number>;
  findingsBySeverity?: Record<string, number>;
  cached?: boolean;
  error?: string;
}

export interface FinancialRiskFindingsResponse {
  success: boolean;
  findings: FinancialRiskFinding[];
  total: number;
  error?: string;
}

export interface FetchFinancialRiskParams {
  severity?: string;
  flagType?: string;
  ruleId?: string;
  articleNumber?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}

export async function runFinancialRiskAnalysis(
  contractId: string,
  ruleIds?: string[]
): Promise<FinancialRiskAnalysisResponse> {
  console.log('[API] Running financial risk analysis for:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analyze/financial-risks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractId, ruleIds }),
  });

  const data = await response.json();
  console.log('[API] Financial risk analysis response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to run financial risk analysis');
  }

  return data;
}

export async function fetchFinancialRisks(
  contractId: string,
  params?: FetchFinancialRiskParams
): Promise<FinancialRiskFindingsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.flagType) searchParams.set('flagType', params.flagType);
  if (params?.ruleId) searchParams.set('ruleId', params.ruleId);
  if (params?.articleNumber) searchParams.set('articleNumber', params.articleNumber);
  if (params?.keyword) searchParams.set('keyword', params.keyword);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/documents/${contractId}/financial-risks${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching financial risks from:', url);

  const response = await apiFetch(url);
  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch financial risks');
  }

  return data;
}

// ---------------------------------------------------------------------------
// Contract Ambiguity Analysis
// ---------------------------------------------------------------------------

export interface AmbiguityFinding {
  id: string;
  clauseId: string;
  clauseText: string;
  articleNumber: string;
  sectionNumber: string;
  sectionTitle: string;
  ruleId: string;
  ruleName: string;
  flagType: string;
  severity: string;
  matchText: string;
  charOffsetStart: number;
  charOffsetEnd: number;
  rationale: string;
}

export interface AmbiguityAnalysisResponse {
  success: boolean;
  analysisId: string;
  findings: AmbiguityFinding[];
  totalFindings: number;
  findingsByFlagType?: Record<string, number>;
  findingsBySeverity?: Record<string, number>;
  cached?: boolean;
  error?: string;
}

export interface AmbiguityFindingsResponse {
  success: boolean;
  findings: AmbiguityFinding[];
  total: number;
  error?: string;
}

export interface FetchAmbiguityParams {
  severity?: string;
  flagType?: string;
  ruleId?: string;
  articleNumber?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}

export async function runContractAmbiguityAnalysis(
  contractId: string,
  ruleIds?: string[],
  flagTypes?: string[]
): Promise<AmbiguityAnalysisResponse> {
  console.log('[API] Running contract ambiguity analysis for:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analyze/contract-ambiguity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractId, ruleIds, flagTypes }),
  });

  const data = await response.json();
  console.log('[API] Contract ambiguity analysis response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to run contract ambiguity analysis');
  }

  return data;
}

export async function fetchContractAmbiguityFindings(
  contractId: string,
  params?: FetchAmbiguityParams
): Promise<AmbiguityFindingsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.flagType) searchParams.set('flagType', params.flagType);
  if (params?.ruleId) searchParams.set('ruleId', params.ruleId);
  if (params?.articleNumber) searchParams.set('articleNumber', params.articleNumber);
  if (params?.keyword) searchParams.set('keyword', params.keyword);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/documents/${contractId}/contract-ambiguity${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching contract ambiguity findings from:', url);

  const response = await apiFetch(url);
  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch contract ambiguity findings');
  }

  return data;
}

// ---------------------------------------------------------------------------
// Deadline Clauses Analysis
// ---------------------------------------------------------------------------

export interface DeadlineFinding {
  id: string;
  clauseId: string;
  clauseText: string;
  articleNumber: string;
  sectionNumber: string;
  sectionTitle: string;
  ruleId: string;
  ruleName: string;
  category: string;
  urgency: string;
  matchText: string;
  charOffsetStart: number;
  charOffsetEnd: number;
  rationale: string;
  normalizedDays: number | null;
}

export interface DeadlineAnalysisResponse {
  success: boolean;
  analysisId: string;
  findings: DeadlineFinding[];
  totalFindings: number;
  findingsByCategory?: Record<string, number>;
  findingsByUrgency?: Record<string, number>;
  cached?: boolean;
  error?: string;
}

export interface DeadlineFindingsResponse {
  success: boolean;
  findings: DeadlineFinding[];
  total: number;
  error?: string;
}

export interface FetchDeadlineParams {
  urgency?: string;
  category?: string;
  ruleId?: string;
  articleNumber?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}

export async function runDeadlineClauseAnalysis(
  contractId: string,
  ruleIds?: string[],
  categories?: string[]
): Promise<DeadlineAnalysisResponse> {
  console.log('[API] Running deadline clause analysis for:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analyze/deadline-clauses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractId, ruleIds, categories }),
  });

  const data = await response.json();
  console.log('[API] Deadline clause analysis response:', data);

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to run deadline clause analysis');
  }

  return data;
}

export async function fetchDeadlineClauseFindings(
  contractId: string,
  params?: FetchDeadlineParams
): Promise<DeadlineFindingsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.urgency) searchParams.set('urgency', params.urgency);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.ruleId) searchParams.set('ruleId', params.ruleId);
  if (params?.articleNumber) searchParams.set('articleNumber', params.articleNumber);
  if (params?.keyword) searchParams.set('keyword', params.keyword);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/documents/${contractId}/deadline-clauses${queryString ? `?${queryString}` : ''}`;

  console.log('[API] Fetching deadline clause findings from:', url);

  const response = await apiFetch(url);
  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch deadline clause findings');
  }

  return data;
}

// ---------------------------------------------------------------------------
// Deadline Comparison
// ---------------------------------------------------------------------------

export interface DeadlineComparisonPair {
  sourceClauseId: string;
  sourceClauseText: string;
  sourceArticleNumber: string;
  sourceSectionNumber: string;
  sourceSectionTitle: string;
  sourceMatchText: string;
  sourceNormalizedDays: number | null;
  sourceUrgency: string;
  targetClauseId: string;
  targetClauseText: string;
  targetArticleNumber: string;
  targetSectionNumber: string;
  targetSectionTitle: string;
  targetMatchText: string;
  targetNormalizedDays: number | null;
  targetUrgency: string;
  daysDifference: number | null;
  category: string;
}

export interface DeadlineComparisonResponse {
  success: boolean;
  sourceContractId: string;
  targetContractId: string;
  pairs: DeadlineComparisonPair[];
  totalSourceFindings: number;
  totalTargetFindings: number;
  error?: string;
}

export async function compareDeadlineClauses(
  sourceContractId: string,
  targetContractId: string
): Promise<DeadlineComparisonResponse> {
  console.log('[API] Comparing deadline clauses:', sourceContractId, 'vs', targetContractId);

  const response = await apiFetch(`${API_BASE_URL}/api/analyze/deadline-comparison`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceContractId, targetContractId }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to compare deadline clauses');
  }

  return data;
}

// ---------------------------------------------------------------------------
// Financial Risk Comparison
// ---------------------------------------------------------------------------

export interface ComparisonSuggestion {
  targetClauseId: string;
  targetClauseText: string;
  targetArticleNumber: string;
  targetSectionNumber: string;
  targetSectionTitle: string;
  similarity: number;
  matchReason: string;
}

export interface ComparisonSuggestionsResponse {
  success: boolean;
  suggestions: ComparisonSuggestion[];
  processingTimeMs: number;
  error?: string;
}

export interface ComparisonSession {
  id: string;
  sourceContractId: string;
  targetContractId: string;
  sourceFindingId: string;
  sourceClauseId: string;
  status: string;
  suggestions: ComparisonSuggestion[];
  acceptedClauseIds: string[];
  rejectedClauseIds: string[];
  manuallyAddedClauseIds: string[];
  createdAt: string;
  updatedAt: string;
}

export async function fetchComparisonSuggestions(
  sourceClauseText: string,
  targetContractId: string,
  maxSuggestions?: number
): Promise<ComparisonSuggestionsResponse> {
  console.log('[API] Fetching comparison suggestions for target:', targetContractId);

  const response = await apiFetch(`${API_BASE_URL}/api/financial-risks/compare-suggestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceClauseText, targetContractId, maxSuggestions }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch comparison suggestions');
  }

  return data;
}

export async function createComparisonSession(
  sourceFindingId: string,
  sourceClauseId: string,
  sourceContractId: string,
  targetContractId: string,
  suggestions: ComparisonSuggestion[]
): Promise<{ success: boolean; sessionId: string }> {
  const response = await apiFetch(`${API_BASE_URL}/api/financial-risks/comparison-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sourceFindingId,
      sourceClauseId,
      sourceContractId,
      targetContractId,
      suggestions,
    }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to create comparison session');
  }

  return data;
}

export async function updateComparisonSession(
  sessionId: string,
  updates: {
    acceptedClauseIds?: string[];
    rejectedClauseIds?: string[];
    manuallyAddedClauseIds?: string[];
  }
): Promise<void> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/financial-risks/comparison-session/${sessionId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to update comparison session');
  }
}

// ============================================================
// R2 File Inventory & Pipeline Viewer API
// ============================================================

export interface R2FileListResponse {
  count: number;
  objects: Array<{ key: string; size: number; uploaded: string }>;
}

export interface ExtractedArticleFromR2 {
  article: {
    number: string;
    title: string;
    summary: string;
    sections: Array<{
      number: string;
      title: string;
      clauses: Array<{
        number?: string;
        text: string;
        cfrCitations?: string[];
      }>;
    }>;
  };
  extractedAt: string;
  processingTime?: number;
  tokensUsed?: { input: number; output: number };
  chunked?: boolean;
  totalChunks?: number;
}

/**
 * Fetch R2 file inventory for a contract
 */
export async function fetchR2FileList(contractId: string): Promise<R2FileListResponse> {
  console.log('[API] Fetching R2 file list for contract:', contractId);

  const response = await apiFetch(
    `${API_BASE_URL}/api/r2/list?prefix=contracts/${encodeURIComponent(contractId)}/`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch R2 file list' }));
    throw new Error(error.error || 'Failed to fetch R2 file list');
  }

  const data = await response.json();
  return data;
}

/**
 * Fetch OCR processed text for a contract
 */
export async function fetchProcessedText(contractId: string): Promise<string> {
  console.log('[API] Fetching processed text for contract:', contractId);

  const response = await apiFetch(
    `${API_BASE_URL}/api/r2/download?key=contracts/${encodeURIComponent(contractId)}/processed.json`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch processed text' }));
    throw new Error(error.error || 'Failed to fetch processed text');
  }

  const data = await response.json();
  return data.text || '';
}

/**
 * Fetch a single extracted article JSON from R2
 */
export async function fetchExtractedArticle(
  contractId: string,
  articleNum: string
): Promise<ExtractedArticleFromR2> {
  console.log('[API] Fetching extracted article', articleNum, 'for contract:', contractId);

  // Sanitize article number for use in R2 key
  const safeNum = articleNum.replace(/[^a-zA-Z0-9._-]/g, '_');

  const response = await apiFetch(
    `${API_BASE_URL}/api/r2/download?key=contracts/${encodeURIComponent(contractId)}/articles/article-${encodeURIComponent(safeNum)}.json`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: `Failed to fetch article ${articleNum}` }));
    throw new Error(error.error || `Failed to fetch article ${articleNum}`);
  }

  const data = await response.json();
  return data;
}

export async function fetchComparisonSession(
  sessionId: string
): Promise<{ success: boolean; session: ComparisonSession }> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/financial-risks/comparison-session/${sessionId}`
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch comparison session');
  }

  return data;
}

// ============================================================
// Pipeline Health API
// ============================================================

export interface PipelineArticleStatus {
  number: string;
  title: string;
  inR2: boolean;
  inDB: boolean;
  status: 'ok' | 'not_stored' | 'missing';
  coveredBy?: 'definitions' | 'acronyms';
  errorType?: 'boundary_not_found' | 'extraction_timeout' | 'api_error' | 'parse_failed' | 'max_tokens' | 'expired' | 'unknown';
  errorMessage?: string;
  lastAttemptAt?: string;
  attemptCount?: number;
}

export interface PipelineHealthResponse {
  success: boolean;
  documentId: string;
  tocArticles: number;
  r2ArticleFiles: number;
  dbArticles: number;
  missingFromR2: string[];
  missingFromDB: string[];
  ocrPageCount: number;
  ocrPagesOrdered: boolean;
  articleStatuses: PipelineArticleStatus[];
  issues: string[];
  activeBatch?: {
    batchId: string;
    submittedAt: string;
    totalArticles: number;
  } | null;
  definitionsR2Count?: number;
  definitionsDbCount?: number;
  acronymsR2Count?: number;
  acronymsDbCount?: number;
  error?: string;
}

/**
 * Fetch pipeline health diagnostics for a contract
 */
export async function fetchPipelineHealth(contractId: string): Promise<PipelineHealthResponse> {
  console.log('[API] Fetching pipeline health for contract:', contractId);

  const response = await apiFetch(
    `${API_BASE_URL}/api/documents/${contractId}/pipeline-health`
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch pipeline health' }));
    throw new Error(error.error || 'Failed to fetch pipeline health');
  }

  const data: PipelineHealthResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'API returned unsuccessful response');
  }

  return data;
}

/**
 * Re-store structure in Supabase (re-attempt after store-structure fix)
 */
export async function restoreStructure(contractId: string): Promise<StoreStructureResponse> {
  console.log('[API] Re-storing structure for contract:', contractId);

  const response = await apiFetch(`${API_BASE_URL}/api/documents/${contractId}/store-structure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fromR2: true }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to re-store structure');
  }

  return data;
}

/**
 * Re-extract missing articles only (legacy: recalculates boundaries + queues single-article path)
 */
export async function reextractMissing(contractId: string): Promise<QueueExtractionResponse> {
  console.log('[API] Re-extracting missing articles for contract:', contractId);

  const response = await apiFetch(
    `${API_BASE_URL}/api/documents/${contractId}/queue-extraction?force=true`,
    { method: 'POST' }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to re-extract missing articles');
  }

  return data;
}

/**
 * Re-extract missing articles via batch API (only submits articles without R2 files)
 */
export async function reextractMissingBatch(contractId: string): Promise<{ success: boolean; totalArticles: number; batchId?: string; skippedExisting?: number; message?: string }> {
  console.log('[API] Re-extracting missing articles via batch for contract:', contractId);

  const response = await apiFetch(
    `${API_BASE_URL}/api/documents/${contractId}/submit-extraction-batch?missingOnly=true`,
    { method: 'POST' }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to submit batch re-extraction');
  }

  return data;
}

// ---------------------------------------------------------------------------
// Contract Chat (RAG)
// ---------------------------------------------------------------------------

export interface CitationFromAPI {
  chunkId: string;
  articleNumber?: string;
  sectionPath?: string;
  text: string;
  similarity: number;
  page?: number;
}

export interface ContractChatResponseFromAPI {
  contractId: string;
  contractName?: string;
  answer: string;
  citations: CitationFromAPI[];
}

export interface ChatResponseFromAPI {
  success: boolean;
  responses: ContractChatResponseFromAPI[];
  comparisonAnalysis?: string;
  error?: string;
}

export interface ChatRequestParams {
  message: string;
  contractIds: string[];
  conversationHistory?: Array<{ role: string; content: string }>;
}

// ---------------------------------------------------------------------------
// Chat History (Persistent Conversations)
// ---------------------------------------------------------------------------

export interface PersistedChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  responses?: ContractChatResponseFromAPI[];
  comparisonAnalysis?: string;
  timestamp: string; // ISO 8601
}

export interface ConversationSummary {
  id: string;
  title: string;
  contractIds: string[];
  dualMode: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationFull extends ConversationSummary {
  messages: PersistedChatMessage[];
}

export async function listChatConversations(options?: {
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<{ conversations: ConversationSummary[]; total?: number }> {
  const params = new URLSearchParams();
  if (options?.q) params.set('q', options.q);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();

  const response = await apiFetch(`${API_BASE_URL}/api/chat/conversations${qs ? '?' + qs : ''}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to list conversations');
  }
  return { conversations: data.conversations, total: data.total };
}

export async function getChatConversation(id: string): Promise<ConversationFull> {
  const response = await apiFetch(`${API_BASE_URL}/api/chat/conversations/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get conversation');
  }
  return data.conversation;
}

export async function createChatConversation(params: {
  title: string;
  contractIds: string[];
  dualMode: boolean;
  messages: PersistedChatMessage[];
}): Promise<ConversationFull> {
  const response = await apiFetch(`${API_BASE_URL}/api/chat/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to create conversation');
  }
  return data.conversation;
}

export async function updateChatConversation(
  id: string,
  params: { title?: string; messages?: PersistedChatMessage[] },
): Promise<ConversationFull> {
  const response = await apiFetch(`${API_BASE_URL}/api/chat/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to update conversation');
  }
  return data.conversation;
}

export async function deleteChatConversation(id: string): Promise<void> {
  const response = await apiFetch(`${API_BASE_URL}/api/chat/conversations/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to delete conversation');
  }
}

export async function chatWithContracts(params: ChatRequestParams): Promise<ChatResponseFromAPI> {
  console.log('[API] Chat with contracts:', params.contractIds, 'message:', params.message.slice(0, 80));

  const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: params.message,
      contractIds: params.contractIds,
      conversationHistory: params.conversationHistory,
    }),
  });

  const data: ChatResponseFromAPI = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Chat request failed');
  }

  return data;
}

// ─── Regulatory Document Analysis API ────────────────────────────────────────

export interface RegulatoryDocument {
  id: string;
  name: string;
  description: string | null;
  source: string | null;
  status: string;
  total_requirements: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface RegulatoryRequirement {
  id: string;
  requirement_number: string;
  text: string;
  section_reference: string | null;
  page_reference: string | null;
  category: string;
  subcategory: string | null;
  priority: string;
  cfr_citations: string[];
  applicability_notes: string | null;
  created_at: string;
}

export interface ComplianceAnalysis {
  id: string;
  regulatory_doc_id: string;
  contract_id: string;
  status: string;
  summary: ComplianceSummary | null;
  created_at: string;
  completed_at: string | null;
  regulatory_documents?: { name: string; source: string };
}

export interface ComplianceSummary {
  total_requirements: number;
  met: number;
  partially_met: number;
  not_met: number;
  not_applicable: number;
  overall_score: number;
}

export interface MatchedClause {
  id: string;
  text: string | null;
  article_number: string | null;
  section_number: string | null;
}

export interface ComplianceFinding {
  id: string;
  requirement_id: string;
  compliance_status: 'met' | 'partially_met' | 'not_met' | 'not_applicable';
  confidence: number;
  evidence_text: string | null;
  matched_clause_ids: string[];
  matched_clauses: MatchedClause[];
  gap_description: string | null;
  recommendation: string | null;
  reasoning: string | null;
  created_at: string;
  regulatory_requirements: {
    requirement_number: string;
    text: string;
    category: string;
    priority: string;
    section_reference: string | null;
  };
}

export async function fetchRegulatoryDocuments(): Promise<RegulatoryDocument[]> {
  console.log('[API] Fetching regulatory documents');
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch regulatory documents');
  }
  return data.documents;
}

export async function uploadRegulatoryDocument(
  file: File,
  metadata: { name: string; description?: string; source?: string }
): Promise<{ documentId: string }> {
  console.log('[API] Uploading regulatory document:', metadata.name);
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', metadata.name);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.source) formData.append('source', metadata.source);

  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents`, {
    method: 'POST',
    body: formData,
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to upload regulatory document');
  }
  return { documentId: data.documentId };
}

export async function processRegulatoryDocument(docId: string): Promise<void> {
  console.log('[API] Processing regulatory document:', docId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}/process`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to process document');
  }
}

export interface ConvertMarkdownChunkResult {
  success: boolean;
  documentId: string;
  chunkIndex: number;
  totalChunks: number;
  done: boolean;
  error?: string;
}

/**
 * Convert one chunk of regulatory document markdown via SSE stream.
 * The backend streams progress events to keep the connection alive,
 * then sends a final `done` or `error` event.
 *
 * @param onProgress - optional callback receiving character count as Claude generates text
 */
export async function convertRegulatoryDocMarkdownChunk(
  docId: string,
  onProgress?: (chars: number) => void
): Promise<ConvertMarkdownChunkResult> {
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}/convert-markdown`, {
    method: 'POST',
  });

  if (!response.ok) {
    // Non-streaming error (e.g. 500 before stream starts)
    let errorMsg = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      errorMsg = text || errorMsg;
    } catch { /* ignore */ }
    throw new Error(errorMsg);
  }

  // Read SSE stream
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    let currentEvent = '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
        continue;
      }
      if (!line.startsWith('data: ')) continue;
      const dataStr = line.slice(6);

      try {
        const data = JSON.parse(dataStr);

        if (currentEvent === 'progress' && onProgress && typeof data.chars === 'number') {
          onProgress(data.chars);
        } else if (currentEvent === 'done') {
          return {
            success: true,
            documentId: docId,
            chunkIndex: data.chunkIndex,
            totalChunks: data.totalChunks,
            done: data.done,
          };
        } else if (currentEvent === 'error') {
          throw new Error(data.error || 'Markdown conversion failed');
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue; // skip malformed JSON
        throw e;
      }
      currentEvent = '';
    }
  }

  // Stream ended without a done/error event
  throw new Error('Stream ended unexpectedly without completion');
}

export async function extractRegulatoryRequirements(docId: string): Promise<{ requirementsExtracted: number }> {
  console.log('[API] Extracting requirements from:', docId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}/extract-requirements`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to extract requirements');
  }
  return { requirementsExtracted: data.requirementsExtracted };
}

export async function fetchRegulatoryRequirements(
  docId: string,
  filters?: { category?: string; priority?: string }
): Promise<RegulatoryRequirement[]> {
  console.log('[API] Fetching requirements for:', docId);
  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  if (filters?.priority) params.set('priority', filters.priority);
  const qs = params.toString();
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}/requirements${qs ? `?${qs}` : ''}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch requirements');
  }
  return data.requirements;
}

export async function generateRegulatoryEmbeddings(docId: string): Promise<void> {
  console.log('[API] Generating embeddings for requirements:', docId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}/generate-embeddings`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to generate embeddings');
  }
}

export async function deleteRegulatoryDocument(docId: string): Promise<void> {
  console.log('[API] Deleting regulatory document:', docId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-documents/${docId}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to delete document');
  }
}

export interface StartComplianceAnalysisResult {
  success: boolean;
  analysisId: string;
  summary: ComplianceSummary;
  findingsCount: number;
}

export async function startComplianceAnalysis(
  regulatoryDocId: string,
  contractId: string
): Promise<StartComplianceAnalysisResult> {
  console.log('[API] Starting compliance analysis:', regulatoryDocId, 'vs', contractId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-compliance/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ regulatoryDocId, contractId }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to start compliance analysis');
  }
  return data;
}

export async function fetchComplianceAnalysis(analysisId: string): Promise<ComplianceAnalysis> {
  console.log('[API] Fetching compliance analysis:', analysisId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-compliance/${analysisId}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch compliance analysis');
  }
  return data.analysis;
}

export async function fetchComplianceFindings(
  analysisId: string,
  filters?: { status?: string; category?: string }
): Promise<ComplianceFinding[]> {
  console.log('[API] Fetching compliance findings:', analysisId);
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  const qs = params.toString();
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-compliance/${analysisId}/findings${qs ? `?${qs}` : ''}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch compliance findings');
  }
  return data.findings;
}

export async function fetchComplianceByContract(contractId: string): Promise<ComplianceAnalysis[]> {
  console.log('[API] Fetching compliance analyses for contract:', contractId);
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-compliance/by-contract/${contractId}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch compliance analyses');
  }
  return data.analyses;
}

export async function fetchComplianceAnalyses(
  filters?: { contractId?: string; regulatoryDocId?: string; limit?: number }
): Promise<ComplianceAnalysis[]> {
  console.log('[API] Fetching compliance analyses');
  const params = new URLSearchParams();
  if (filters?.contractId) params.set('contractId', filters.contractId);
  if (filters?.regulatoryDocId) params.set('regulatoryDocId', filters.regulatoryDocId);
  if (filters?.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const response = await apiFetch(`${API_BASE_URL}/api/regulatory-compliance/analyses${qs ? `?${qs}` : ''}`);
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to fetch compliance analyses');
  }
  return data.analyses;
}
